import React, { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { formatCurrency, formatNumber } from '../utils/formatters';

// ==================== TREND INDICATOR COMPONENT ====================
export const TrendIndicator = ({ current, previous, label, format = 'number', positiveIsGood = true }) => {
  if (previous === undefined || previous === null || previous === 0) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-xs text-zinc-500">vs prev</span>
        <span className="text-xs text-zinc-400">—</span>
      </div>
    );
  }

  const change = ((current - previous) / previous) * 100;
  const isPositive = change > 0;
  const isGood = positiveIsGood ? isPositive : !isPositive;
  const isNeutral = Math.abs(change) < 1;

  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5 ${
        isNeutral 
          ? 'bg-zinc-500/20 text-zinc-400' 
          : isGood 
            ? 'bg-emerald-500/20 text-emerald-400' 
            : 'bg-red-500/20 text-red-400'
      }`}>
        {!isNeutral && (
          <svg className={`w-2.5 h-2.5 ${isPositive ? '' : 'rotate-180'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        )}
        {Math.abs(change).toFixed(1)}%
      </span>
      <span className="text-[10px] text-zinc-500">{label || 'vs prev'}</span>
    </div>
  );
};

// ==================== SPEND TREND CHART ====================
export const SpendTrendChart = ({ data, platform = 'META' }) => {
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.slice(-14).map(item => ({
      date: item.date,
      spend: item.spend || 0,
      leads: item.leads || 0,
      messages: item.messaging_conversations || 0,
      conversions: item.conversions || 0,
    }));
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-zinc-500">
        No trend data available
      </div>
    );
  }

  const colors = platform === 'META' 
    ? { spend: '#1877F2', leads: '#10B981', messages: '#8B5CF6' }
    : { spend: '#4285F4', conversions: '#34A853' };

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={`spendGradient${platform}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colors.spend} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={colors.spend} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#71717A', fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: '#27272A' }}
            tickFormatter={(value) => value?.slice(5) || ''} // Show MM-DD
          />
          <YAxis 
            tick={{ fill: '#71717A', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => formatCurrency(value)}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#18181B', 
              border: '1px solid #27272A',
              borderRadius: '8px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
            }}
            labelStyle={{ color: '#A1A1AA', marginBottom: '8px' }}
            formatter={(value, name) => [
              name === 'spend' ? formatCurrency(value) : formatNumber(value),
              name.charAt(0).toUpperCase() + name.slice(1)
            ]}
          />
          <Area 
            type="monotone" 
            dataKey="spend" 
            stroke={colors.spend} 
            fill={`url(#spendGradient${platform})`}
            strokeWidth={2}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== CAMPAIGN BREAKDOWN PIE CHART ====================
export const CampaignBreakdownChart = ({ campaigns, metric = 'spend' }) => {
  const chartData = useMemo(() => {
    if (!campaigns || !Array.isArray(campaigns)) return [];
    
    return campaigns
      .filter(c => (c[metric] || 0) > 0)
      .sort((a, b) => (b[metric] || 0) - (a[metric] || 0))
      .slice(0, 5)
      .map((campaign, idx) => ({
        name: campaign.name?.length > 20 
          ? campaign.name.substring(0, 20) + '...' 
          : campaign.name,
        value: campaign[metric] || 0,
        fullName: campaign.name,
      }));
  }, [campaigns, metric]);

  const COLORS = ['#1877F2', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-zinc-500 text-sm">
        No campaign data
      </div>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={70}
            paddingAngle={2}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#18181B', 
              border: '1px solid #27272A',
              borderRadius: '8px',
            }}
            formatter={(value) => [
              metric === 'spend' ? formatCurrency(value) : formatNumber(value),
              `${((value / total) * 100).toFixed(1)}%`
            ]}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== HEALTH STATUS DISTRIBUTION CHART ====================
export const HealthStatusChart = ({ clients }) => {
  const chartData = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];
    
    const statusCounts = {
      healthy: clients.filter(c => c.health_status === 'healthy').length,
      warning: clients.filter(c => c.health_status === 'warning').length,
      critical: clients.filter(c => c.health_status === 'critical').length,
    };

    return [
      { name: 'Healthy', value: statusCounts.healthy, fill: '#10B981' },
      { name: 'Warning', value: statusCounts.warning, fill: '#F59E0B' },
      { name: 'Critical', value: statusCounts.critical, fill: '#EF4444' },
    ].filter(d => d.value > 0);
  }, [clients]);

  if (chartData.length === 0) {
    return (
      <div className="h-40 flex items-center justify-center text-zinc-500 text-sm">
        No health data
      </div>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="h-40">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={35}
            outerRadius={60}
            paddingAngle={3}
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#18181B', 
              border: '1px solid #27272A',
              borderRadius: '8px',
            }}
            formatter={(value, name) => [
              `${value} (${((value / total) * 100).toFixed(0)}%)`,
              name
            ]}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== CLIENT PERFORMANCE BAR CHART ====================
export const ClientPerformanceChart = ({ clients, metric = 'spend', limit = 10 }) => {
  const chartData = useMemo(() => {
    if (!clients || !Array.isArray(clients)) return [];
    
    return clients
      .filter(c => (c.metrics?.[metric] || 0) > 0)
      .slice(0, limit)
      .map(client => ({
        name: client.name?.length > 15 
          ? client.name.substring(0, 15) + '...' 
          : client.name,
        [metric]: client.metrics?.[metric] || 0,
        score: client.health_score || 0,
        fullName: client.name,
      }));
  }, [clients, metric, limit]);

  if (chartData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-zinc-500">
        No client data available
      </div>
    );
  }

  const metricColors = {
    spend: '#1877F2',
    leads: '#10B981',
    conversions: '#F59E0B',
    clicks: '#8B5CF6'
  };

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272A" horizontal={false} />
          <XAxis 
            type="number" 
            tick={{ fill: '#71717A', fontSize: 10 }}
            tickLine={false}
            axisLine={{ stroke: '#27272A' }}
            tickFormatter={(value) => metric === 'spend' ? formatCurrency(value) : formatNumber(value)}
          />
          <YAxis 
            type="category" 
            dataKey="name" 
            tick={{ fill: '#A1A1AA', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={100}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#18181B', 
              border: '1px solid #27272A',
              borderRadius: '8px',
            }}
            labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
            formatter={(value) => [
              metric === 'spend' ? formatCurrency(value) : formatNumber(value),
              metric.charAt(0).toUpperCase() + metric.slice(1)
            ]}
          />
          <Bar 
            dataKey={metric} 
            fill={metricColors[metric] || '#1877F2'}
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== OBJECTIVE DISTRIBUTION CHART ====================
export const ObjectiveDistributionChart = ({ campaigns, clients }) => {
  const chartData = useMemo(() => {
    // First try to use client campaigns which may have spend data
    const allCampaigns = clients?.flatMap(c => c.campaigns || []) || campaigns || [];
    
    if (!allCampaigns.length) return [];
    
    const objectiveMap = {};
    allCampaigns.forEach(campaign => {
      const obj = campaign.objective || 'UNKNOWN';
      const displayName = getObjectiveDisplayName(obj);
      if (!objectiveMap[displayName]) {
        objectiveMap[displayName] = { count: 0 };
      }
      objectiveMap[displayName].count += 1;
    });

    return Object.entries(objectiveMap)
      .map(([name, data]) => ({
        name,
        count: data.count,
      }))
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [campaigns, clients]);

  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-zinc-500 text-sm">
        No objective data
      </div>
    );
  }

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
          <XAxis 
            dataKey="name" 
            tick={{ fill: '#71717A', fontSize: 9 }}
            tickLine={false}
            axisLine={{ stroke: '#27272A' }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            tick={{ fill: '#71717A', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#18181B', 
              border: '1px solid #27272A',
              borderRadius: '8px',
            }}
            formatter={(value) => [formatNumber(value), 'Campaigns']}
          />
          <Bar dataKey="count" fill="#1877F2" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== METRIC COMPARISON CHART ====================
export const MetricComparisonChart = ({ currentPeriod, previousPeriod, metrics = ['spend', 'leads'] }) => {
  const chartData = useMemo(() => {
    if (!currentPeriod) return [];
    
    return metrics.map(metric => ({
      name: metric.charAt(0).toUpperCase() + metric.slice(1),
      current: currentPeriod[metric] || 0,
      previous: previousPeriod?.[metric] || 0,
    }));
  }, [currentPeriod, previousPeriod, metrics]);

  if (chartData.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center text-zinc-500">
        No comparison data
      </div>
    );
  }

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
          <XAxis 
            dataKey="name" 
            tick={{ fill: '#71717A', fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#27272A' }}
          />
          <YAxis 
            tick={{ fill: '#71717A', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(value) => formatNumber(value)}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#18181B', 
              border: '1px solid #27272A',
              borderRadius: '8px',
            }}
          />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="circle"
          />
          <Bar dataKey="current" name="Current" fill="#1877F2" radius={[4, 4, 0, 0]} />
          <Bar dataKey="previous" name="Previous" fill="#71717A" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ==================== HELPER FUNCTIONS ====================
const getObjectiveDisplayName = (objective) => {
  const mapping = {
    'OUTCOME_LEADS': 'Lead Form',
    'LEAD_GENERATION': 'Lead Form',
    'OUTCOME_ENGAGEMENT': 'Engagement',
    'MESSAGES': 'Messages',
    'POST_ENGAGEMENT': 'Engagement',
    'OUTCOME_TRAFFIC': 'Traffic',
    'LINK_CLICKS': 'Traffic',
    'OUTCOME_SALES': 'Sales',
    'CONVERSIONS': 'Conversions',
    'OUTCOME_AWARENESS': 'Awareness',
    'REACH': 'Reach',
    'VIDEO_VIEWS': 'Video',
    'SEARCH': 'Search',
    'DISPLAY': 'Display',
    'PERFORMANCE_MAX': 'PMax',
    'SHOPPING': 'Shopping',
    'VIDEO': 'Video',
    'DISCOVERY': 'Discovery',
  };
  return mapping[objective] || objective || 'Other';
};

// ==================== CHARTS DASHBOARD COMPONENT ====================
const PerformanceCharts = ({ clients, platform = 'META', campaigns = [] }) => {
  const [activeMetric, setActiveMetric] = useState('spend');
  const [showCharts, setShowCharts] = useState(false);

  // Aggregate all campaigns from clients
  const allCampaigns = useMemo(() => {
    if (campaigns.length > 0) return campaigns;
    return clients?.flatMap(client => client.campaigns || []) || [];
  }, [clients, campaigns]);

  // Summary metrics with trends
  const summaryWithTrends = useMemo(() => {
    const totalSpend = clients?.reduce((sum, c) => sum + (c.metrics?.spend || 0), 0) || 0;
    const totalLeads = clients?.reduce((sum, c) => sum + (c.metrics?.leads || 0), 0) || 0;
    const totalMessages = clients?.reduce((sum, c) => {
      const camps = c.campaigns || [];
      return sum + camps.reduce((s, camp) => s + (camp.messaging_conversations || 0), 0);
    }, 0) || 0;
    
    return {
      spend: totalSpend,
      leads: totalLeads,
      messages: totalMessages,
      results: totalLeads + totalMessages,
    };
  }, [clients]);

  if (!clients || clients.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Toggle Button */}
      <button
        onClick={() => setShowCharts(!showCharts)}
        className={`w-full p-4 rounded-xl border transition-colors flex items-center justify-between ${
          showCharts 
            ? 'bg-[#1877F2]/10 border-[#1877F2]/30' 
            : 'bg-white/5 border-white/10 hover:border-white/20'
        }`}
        data-testid="toggle-charts-btn"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            showCharts ? 'bg-[#1877F2]/20' : 'bg-white/10'
          }`}>
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-white">Performance Charts</h3>
            <p className="text-xs text-zinc-500">Interactive visualizations & analytics</p>
          </div>
        </div>
        <svg className={`w-5 h-5 text-zinc-400 transition-transform ${showCharts ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Charts Section */}
      {showCharts && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-fade-in" data-testid="performance-charts-section">
          {/* Top Clients by Spend */}
          <div className="bg-[#09090B] rounded-xl border border-white/10 p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-white">Top Clients by Spend</h4>
              <div className="flex gap-1">
                {['spend', 'leads'].map(m => (
                  <button
                    key={m}
                    onClick={() => setActiveMetric(m)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      activeMetric === m
                        ? 'bg-[#1877F2]/20 text-[#1877F2]'
                        : 'bg-white/5 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <ClientPerformanceChart clients={clients} metric={activeMetric} limit={8} />
          </div>

          {/* Health Status Distribution */}
          <div className="bg-[#09090B] rounded-xl border border-white/10 p-5">
            <h4 className="text-sm font-bold text-white mb-4">Health Status Distribution</h4>
            <div className="flex items-center gap-6">
              <div className="flex-1">
                <HealthStatusChart clients={clients} />
              </div>
              <div className="space-y-3">
                {[
                  { status: 'healthy', label: 'Healthy', color: 'emerald' },
                  { status: 'warning', label: 'Warning', color: 'amber' },
                  { status: 'critical', label: 'Critical', color: 'red' },
                ].map(({ status, label, color }) => {
                  const count = clients.filter(c => c.health_status === status).length;
                  return (
                    <div key={status} className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full bg-${color}-400`}></span>
                      <span className="text-sm text-zinc-400">{label}:</span>
                      <span className={`text-sm font-bold text-${color}-400`}>{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Campaigns by Objective - Meta Only */}
          {platform === 'META' && (
            <div className="bg-[#09090B] rounded-xl border border-white/10 p-5">
              <h4 className="text-sm font-bold text-white mb-4">Campaigns by Objective</h4>
              <ObjectiveDistributionChart campaigns={allCampaigns} clients={clients} />
            </div>
          )}

          {/* Performance Overview */}
          <div className="bg-[#09090B] rounded-xl border border-white/10 p-5">
            <h4 className="text-sm font-bold text-white mb-4">Performance Overview</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-xs text-zinc-500 uppercase mb-1">Total Spend</p>
                <p className="text-2xl font-bold text-white font-mono">{formatCurrency(summaryWithTrends.spend)}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-xs text-zinc-500 uppercase mb-1">Total Results</p>
                <p className="text-2xl font-bold text-emerald-400 font-mono">{formatNumber(summaryWithTrends.results)}</p>
              </div>
              {platform === 'META' && (
                <>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Form Leads</p>
                    <p className="text-xl font-bold text-blue-400 font-mono">{formatNumber(summaryWithTrends.leads)}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Messages</p>
                    <p className="text-xl font-bold text-green-400 font-mono">{formatNumber(summaryWithTrends.messages)}</p>
                  </div>
                </>
              )}
              {platform === 'GOOGLE' && (
                <>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Avg CPA</p>
                    <p className="text-xl font-bold text-amber-400 font-mono">
                      {summaryWithTrends.results > 0 
                        ? formatCurrency(summaryWithTrends.spend / summaryWithTrends.results)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4">
                    <p className="text-xs text-zinc-500 uppercase mb-1">Active Clients</p>
                    <p className="text-xl font-bold text-purple-400 font-mono">{clients.length}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceCharts;
